/**
 * What a section hosted in the preferences window may ask of the window (见 21).
 * The window owns the header, navigation and the overlay layer; a section only
 * publishes state:
 *
 * - `setDrill`: replace the window title and show the header back control while
 *   the section drills (Auto-Delete, Blocked Users). The window hides the
 *   page's other sections until the drill ends.
 * - `setLeaveGuard`: hold navigation while the section has unsaved work. The
 *   window calls the guard with the navigation it was about to do; the guard
 *   calls `proceed` once the user agrees (or right away when nothing is dirty).
 * - `overlayRoot`: where section-owned sheets and confirmations are portaled so
 *   they cover the whole window instead of the section.
 */
import { createContext, type ReactNode, useContext, useMemo } from 'react';

export interface SettingsPaneDrill {
  readonly title: string;
  readonly onBack: () => void;
}

/** Called with the navigation the window is about to do. */
export type SettingsLeaveGuard = (proceed: () => void) => void;

export interface SettingsPaneChrome {
  readonly setDrill: (drill: SettingsPaneDrill | null) => void;
  readonly setLeaveGuard: (guard: SettingsLeaveGuard | null) => void;
  /** `null` until the window has mounted its overlay layer. */
  readonly overlayRoot: HTMLElement | null;
}

const SettingsPaneChromeContext = createContext<SettingsPaneChrome | null>(
  null,
);

export function SettingsPaneChromeProvider({
  setDrill,
  setLeaveGuard,
  overlayRoot,
  children,
}: SettingsPaneChrome & { children: ReactNode }): ReactNode {
  const value = useMemo(
    () => ({ setDrill, setLeaveGuard, overlayRoot }),
    [setDrill, setLeaveGuard, overlayRoot],
  );
  return (
    <SettingsPaneChromeContext.Provider value={value}>
      {children}
    </SettingsPaneChromeContext.Provider>
  );
}

export function useSettingsPaneChrome(): SettingsPaneChrome | null {
  return useContext(SettingsPaneChromeContext);
}
